import React from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Breadcrumb, SEOHead } from '@/components/Shared';
import { productsData } from '@/mocks/products';
import { motion } from 'framer-motion';
import Link from 'next/link';

const siteUrl = 'https://www.truongthofood.vn';

const ProductDetail = () => {
  const router = useRouter();
  const { slug } = router.query;

  const product = productsData.find(p => p.slug === slug);

  if (!product) {
    return (
      <>
        <SEOHead
          title="Không tìm thấy sản phẩm | Trường Thọ Food"
          description="Sản phẩm bạn tìm kiếm không tồn tại"
          noindex
        />
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Không tìm thấy sản phẩm</h1>
          <Link
            href="/products"
            className="text-primary hover:underline"
          >
            Quay lại trang sản phẩm
          </Link>
        </div>
      </>
    );
  }

  const breadcrumbItems = [
    { name: 'Trang chủ', path: '/' },
    { name: 'Sản phẩm', path: '/products' },
    { name: product.name },
  ];

  const productUrl = `${siteUrl}/products/${product.slug}`;

  // Product Schema
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.fullDescription || product.description,
    image: product.images.length > 0 ? product.images : [product.thumbnail],
    category: product.category,
    brand: {
      '@type': 'Brand',
      name: 'Trường Thọ Food',
    },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'VND',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Trường Thọ Food',
      },
    },
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Trang chủ',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Sản phẩm',
        item: `${siteUrl}/products`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: productUrl,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${product.name} - ${product.category} | Trường Thọ Food`}
        description={`${product.description} ${product.fullDescription ? `- ${product.fullDescription.substring(0, 100)}...` : ''}`}
        keywords={`${product.name}, ${product.category}, trứng gà, trứng gà tươi, trứng bóc vỏ sẵn, Trường Thọ Food`}
        image={product.thumbnail}
        url={productUrl}
        type="product"
        structuredData={[productSchema, breadcrumbSchema]}
      />
      <div className="container py-8 md:py-16">
      {/* Breadcrumb */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 md:mb-8"
      >
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
        {/* Image Section */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden"
        >
          <Image
            src={product.images[0] || product.thumbnail}
            alt={product.name}
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </motion.div>

        {/* Content Section */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col gap-6 md:gap-8"
        >
          {/* Product Name */}
          <h1 className="text-[28px] md:text-[36px] lg:text-[42px] font-bold text-gray2 uppercase">
            {product.name}
          </h1>

          {/* Category */}
          <div className="flex items-center gap-2">
            <span className="text-primary text-[14px] md:text-[16px] font-semibold uppercase">
              {product.category}
            </span>
          </div>

          {/* Short Description */}
          <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed">
            {product.description}
          </p>

          {/* Full Description */}
          <div className="pt-4 border-t border-gray-200">
            <h2 className="text-[20px] md:text-[24px] font-semibold text-gray2 mb-4">
              Mô tả chi tiết
            </h2>
            <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed">
              {product.fullDescription}
            </p>
          </div>

          {/* Contact Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="pt-4"
          >
            <Link
              href="/contact"
              className="inline-flex items-center justify-center bg-primary text-white duration-300 w-full md:w-fit text-[16px] md:text-[18px] pt-3 pb-2 px-8 rounded-full hover:text-white hover:bg-transparent relative after:absolute after:inset-0 after:rounded-full after:w-full after:h-full hover:after:border-[1px] hover:after:border-primary"
            >
              Liên hệ ngay
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Additional Images Gallery */}
      {product.images.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 md:mt-16"
        >
          <h2 className="text-[20px] md:text-[24px] font-semibold text-gray2 mb-6">
            Hình ảnh sản phẩm
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {product.images.slice(1).map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className="relative w-full aspect-square rounded-2xl overflow-hidden group cursor-pointer"
              >
                <Image
                  src={image}
                  alt={`${product.name} - Hình ${index + 2}`}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110"
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      </div>
    </>
  );
};

export default ProductDetail;

